import { InMemoryCache } from '@apollo/client/core';

/**
 * Build the Apollo `InMemoryCache` with our typePolicies.
 *
 * Pure module (no Angular DI, no environment/session imports) so the cache
 * identity rules are unit-testable — see `graphql-cache.spec.ts`, which pins
 * the audit-stamp snapshot behaviour. Consumed by `graphql.providers.ts`.
 *
 * There is no cache singleton and no boot-time persistence hydrator: this file
 * used to claim both, and neither existed. The client is built once per app,
 * and `CachePersistService.hydrate()` is deliberately never called — the slim
 * settings port scopes out offline persistence, as `graphql.providers.ts` says
 * in its own header.
 *
 * Worth knowing why that mattered: this whole module sat UNIMPORTED for long
 * enough to be forgotten, so the app ran on Apollo's defaults — no `_id`
 * normalisation at all — while a green spec file tested the dead function and
 * this comment described machinery that was never built.
 */
export function buildCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // ── ID resolution for types that use _id instead of id ──
      // Apollo's default heuristic uses the `id` field. Every GraphQL
      // type that returns `_id` instead MUST have an explicit
      // `keyFields: ['_id']` here, otherwise Apollo will treat each
      // selection set as a separate cache entry — defeating de-duplication
      // and producing the dreaded "Cache data may be lost" warning when
      // two queries return different subsets of the same object.
      //
      // KEEP ALPHABETISED so additions are easy to spot in diffs.
      //
      // AUDIT STAMPS (createdBy / updatedBy) are per-row SNAPSHOTS, not
      // entities: their `_id` is the ACTOR's user id, so Apollo's default
      // `_id` normalisation would merge every stamp authored by the same
      // user into ONE shared cache object — each row's `timestamp` then
      // shows whichever row was fetched last (e.g. a just-created post
      // displaying "2 months ago" from the author's older article).
      // `keyFields: false` keeps each stamp embedded in its parent row.
      ArticleAuditStampType: { keyFields: false },
      ArticleCommentType: { keyFields: ['_id'] },
      ArticleType: {
        keyFields: ['_id'],
        fields: {
          // Stamp selections differ per query (some include `picture`);
          // merge instead of replace so a lighter selection can never
          // wipe fields a richer one already cached on the same article.
          createdBy: { merge: true },
          updatedBy: { merge: true },
        },
      },
      Asset:              { keyFields: ['_id'] },
      AssetType:          { keyFields: ['_id'] },
      BookingType:        { keyFields: ['_id'] },
      BuildingAlertAuditStampType: { keyFields: false },
      BuildingAlertType:  { keyFields: ['_id'] },
      CateringMenuItemType: { keyFields: ['_id'] },
      CateringMenuPublicationType: { keyFields: ['_id'] },
      CateringMenuType: {
        keyFields: ['_id'],
        fields: {
          items: { merge: false }, // Always replace — items array changes on add/delete
        },
      },
      CateringOrderType:  { keyFields: ['_id'] },
      CateringVendorType: { keyFields: ['_id'] },
      CompanyInfoType:    { keyFields: false }, // singleton, no ID
      DigestType:         { keyFields: ['_id'] },
      DivisionType:       { keyFields: ['_id'] },
      FlowInitiativeType: { keyFields: ['_id'] },
      FlowProjectType:    { keyFields: ['_id'] },
      FlowSessionType:    { keyFields: ['_id'] },
      IssueType:          { keyFields: ['_id'] },
      KnowledgeAuditStampType: { keyFields: false },
      MeetingType:        { keyFields: ['_id'] },
      PolicyProposalAuditStampType: { keyFields: false },
      // VALUE OBJECTS WITH AN `id` that is not unique across spaces. Apollo's
      // default heuristic normalises anything with `id`, so every space's
      // `has_image` check shared ONE cache object: visiting a room without
      // an image marked "Primary image" unmet on every other room, under a
      // score that still counted it. Slot ids repeat across spaces (default
      // slots share a canonical id) and a collection point's id is its
      // external space id, empty until set. Keep all three in their space.
      QualityCheckType:   { keyFields: false },
      QuickLinkType:      { keyFields: ['_id'] },
      RoleDefinitionType: { keyFields: ['_id'] },
      SpaceBookingSlot:   { keyFields: false },
      SpaceCollectionPoint: { keyFields: false },
      SpaceIssueType:     { keyFields: ['_id'] },
      SpaceType:          { keyFields: ['_id'] },
      UserQuickLinkType:  { keyFields: ['_id'] },
      ZoneType:           { keyFields: ['_id'] },

      // ── Merge nested objects without IDs ──
      // Prevents "Cache data may be lost" warnings when two queries
      // return different subsets of fields for the same parent object.
      FloorType: {
        fields: {
          mapConfig: { merge: true },
        },
      },
      LocationType: {
        fields: {
          address: { merge: true },
          mapConfig: { merge: true },
        },
      },

      // ── Query-level merge policies ──
      Query: {
        fields: {
          locationOccupancy: { merge: true },
          // Articles: replace on each fetch (paginated list)
          articles: { merge: false },
          // Quick links: always replace (no pagination)
          quickLinks: { merge: false },
          myQuickLinks: { merge: false },
          // Building alerts: replace — admin list is filterable.
          activeBuildingAlerts: { merge: false },
          buildingAlertList: { merge: false },
          buildingAlertsForTarget: { merge: false },
          buildingAlertsOverlappingWindow: { merge: false },
          // Catering orders: replace.
          myCateringOrders: { merge: false },
          cateringOrdersForBooking: { merge: false },
          // Paged issues feed.
          pagedIssues: { merge: false },
          // Divisions admin list.
          divisions: { merge: false },
        },
      },
    },
  });
}
